import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarDays,
  CheckCircle2,
  Download,
  LayoutList,
  Plus,
  Repeat,
  Users,
  Search,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ModaleIntervention } from './ModaleIntervention';
import {
  changerStatuts,
  lireCharge,
  relancerIntervention,
  lireCalendrier,
  lireReferencesIntervention,
  lireIntervention,
  listerInterventions,
  modifierIntervention,
  urlExportInterventions,
  type CalendrierMois,
  type Intervention,
  type PageInterventions,
  type ReferencesIntervention,
  type ReponseCharge,
  type StatutIntervention,
  type TriInterventions,
} from '../api/interventions';
import { BadgeStatut } from '../components/BadgeStatut';
import { Pagination } from '../components/Pagination';
import { useCriteresUrl } from '../hooks/useCriteresUrl';

/** Ce que l'écran sait afficher. `donnees` survit au chargement : voir plus bas. */
interface Etat {
  readonly donnees?: PageInterventions;
  readonly chargement: boolean;
  readonly erreur?: string;
  /** Page RÉELLEMENT demandée pour ces données : sert à corriger l'URL, jamais à la deviner. */
  readonly pageDemandee?: number;
}

const OPTIONS_STATUT: ReadonlyArray<{ valeur: StatutIntervention | ''; libelle: string }> = [
  { valeur: '', libelle: 'Tous les statuts' },
  { valeur: 'planifiee', libelle: 'Planifiée' },
  { valeur: 'en-cours', libelle: 'En cours' },
  { valeur: 'terminee', libelle: 'Terminée' },
  { valeur: 'annulee', libelle: 'Annulée' },
];

/** Délai avant d'interroger le serveur pendant la frappe. */
const DELAI_RECHERCHE_MS = 300;

/** Le détail vient du serveur : il a ses propres états, comme la liste. */
type Detail =
  | { readonly phase: 'ferme' }
  | { readonly phase: 'chargement'; readonly id: string }
  | { readonly phase: 'pret'; readonly intervention: Intervention }
  | { readonly phase: 'panne'; readonly id: string; readonly message: string };

/** « 2026-10-28 » → « 28/10/2026 » (affichage seul, aucune conversion). */
function dateFrancaise(iso: string): string {
  const [annee = '', mois = '', jour = ''] = iso.split('-');
  if (annee === '' || mois === '' || jour === '') return iso;
  return `${jour}/${mois}/${annee}`;
}

export function MesInterventions() {
  const {
    recherche,
    statut,
    retard,
    periode,
    date,
    tri,
    sens,
    page,
    setRecherche,
    setStatut,
    setRetard,
    setPeriode,
    setDate,
    setTri,
    setPage,
    toutEffacer,
  } = useCriteresUrl();
  const [saisie, setSaisie] = useState(recherche);
  /** Trois présentations de la même donnée : la liste, le mois, la charge. */
  const [vue, setVue] = useState<'liste' | 'calendrier' | 'charge'>('liste');
  const [charge, setCharge] = useState<{
    donnees?: ReponseCharge;
    chargement: boolean;
    erreur?: string;
  }>({ chargement: false });
  /** Semaine demandée ; `undefined` = semaine courante, décidée par le serveur. */
  const [semaineCharge, setSemaineCharge] = useState<string>();
  const [calendrier, setCalendrier] = useState<{
    donnees?: CalendrierMois;
    chargement: boolean;
    erreur?: string;
  }>({ chargement: false });
  /** Les lignes cochées, vidées à chaque changement de critère. */
  const [selection, setSelection] = useState<readonly string[]>([]);
  const [erreurAction, setErreurAction] = useState<string>();
  /** Mois affiché dans le calendrier ; `undefined` = mois courant (décidé serveur). */
  const [moisAffiche, setMoisAffiche] = useState<string>();
  /** Jour sur lequel ouvrir le modal, quand on clique une case du calendrier. */
  const [jourChoisi, setJourChoisi] = useState<string>();
  const [etat, setEtat] = useState<Etat>({ chargement: true });
  const [detail, setDetail] = useState<Detail>({ phase: 'ferme' });
  const [creation, setCreation] = useState(false);
  const [confirmation, setConfirmation] = useState<string>();
  /** Changement de statut en cours : l'écran n'invente jamais l'état d'après. */
  const [statutChoisi, setStatutChoisi] = useState<StatutIntervention>();
  const [enregistrementStatut, setEnregistrementStatut] = useState(false);
  const [erreurStatut, setErreurStatut] = useState<string>();
  /** Motif d'annulation : exigé par le serveur dès que le statut visé est « annulée ». */
  const [motif, setMotif] = useState('');
  /** Date de relance d'un contrôle annulé. */
  const [dateRelance, setDateRelance] = useState('');
  const [relance, setRelance] = useState(false);
  /** Édition du planning dans la fiche : heure, durée, technicien, consignes. */
  const [planning, setPlanning] = useState<{
    heure: string;
    duree: number;
    technicien: string;
    notes: string;
  }>();
  const [refsPlanning, setRefsPlanning] = useState<ReferencesIntervention>();
  const [erreurPlanning, setErreurPlanning] = useState<string>();
  const [enregistrementPlanning, setEnregistrementPlanning] = useState(false);

  // Le champ suit l'URL : un Retour arriere doit remettre le texte d'avant.
  useEffect(() => {
    setSaisie(recherche);
  }, [recherche]);

  // La frappe ne declenche pas une requete par caractere, et c'est l'URL qui
  // porte le critere applique — pas un etat intermediaire a resynchroniser.
  useEffect(() => {
    if (saisie === recherche) return;
    const minuteur = globalThis.setTimeout(() => setRecherche(saisie.trim()), DELAI_RECHERCHE_MS);
    return () => globalThis.clearTimeout(minuteur);
  }, [saisie, recherche, setRecherche]);

  const charger = useCallback(
    async (signal: AbortSignal) => {
      // Les lignes precedentes restent affichees pendant le chargement : les
      // remplacer par « Chargement… » fait clignoter l'ecran a chaque frappe.
      setEtat((precedent) => ({ ...precedent, chargement: true, erreur: undefined }));

      const resultat = await listerInterventions(
        { recherche, statut, retard, periode, date, tri, sens, page },
        { signal },
      );
      if (resultat.ok && resultat.donnees) {
        setEtat({ donnees: resultat.donnees, chargement: false, pageDemandee: page });
      } else {
        setEtat((precedent) => ({
          ...precedent,
          chargement: false,
          erreur: resultat.message ?? 'Erreur inattendue.',
        }));
      }
    },
    [recherche, statut, retard, periode, date, tri, sens, page],
  );

  useEffect(() => {
    const controleur = new AbortController();
    void charger(controleur.signal);
    return () => controleur.abort();
  }, [charger]);

  // Le détail est DEMANDÉ au serveur, pas recopié depuis la ligne de la liste.
  const ouvrirDetail = useCallback(async (id: string) => {
    setStatutChoisi(undefined);
    setErreurStatut(undefined);
    setDetail({ phase: 'chargement', id });
    const resultat = await lireIntervention(id);
    if (resultat.ok && resultat.donnees) {
      setDetail({ phase: 'pret', intervention: resultat.donnees });
    } else {
      setDetail({ phase: 'panne', id, message: resultat.message ?? 'Erreur inattendue.' });
    }
  }, []);

  // Le modal charge SES listes (`/api/interventions/references`) : clients,
  // sites, types, techniciens, durées et défauts viennent du serveur, en une fois.
  const ouvrirCreation = useCallback(() => {
    setConfirmation(undefined);
    setCreation(true);
  }, []);

  /** Enregistre le planning : le serveur recalcule la fin et revérifie l'agenda. */
  const enregistrerPlanning = useCallback(async () => {
    if (detail.phase !== 'pret' || !planning) return;
    setEnregistrementPlanning(true);
    setErreurPlanning(undefined);

    const resultat = await modifierIntervention(detail.intervention.id, {
      heureDebut: planning.heure,
      dureeMinutes: planning.duree,
      technicienId: planning.technicien,
      notes: planning.notes,
    });
    setEnregistrementPlanning(false);

    if (resultat.ok && resultat.donnees) {
      setDetail({ phase: 'pret', intervention: resultat.donnees });
      setConfirmation(
        `Planning enregistré : ${resultat.donnees.heureDebut ?? 'sans créneau'}` +
          (resultat.donnees.fin ? ` → ${resultat.donnees.fin}` : '') +
          (resultat.donnees.technicien ? `, ${resultat.donnees.technicien}` : '') +
          '.',
      );
      void charger(new AbortController().signal);
      // Le calendrier se rafraîchit par l'effet qui dépend de `vue` : on ne
      // rappelle pas `chargerCalendrier` ici (déclaré plus bas, et déjà à jour).
    } else {
      // Le refus du serveur est repris tel quel : conflit d'agenda, heure illisible…
      setErreurPlanning(resultat.message ?? 'Enregistrement refusé.');
    }
  }, [detail, planning, charger]);

  /** Relance un contrôle annulé : le serveur copie la fiche à la nouvelle date. */
  const relancer = useCallback(async () => {
    if (detail.phase !== 'pret') return;
    setRelance(true);
    setErreurStatut(undefined);
    const resultat = await relancerIntervention(detail.intervention.id, dateRelance);
    setRelance(false);
    if (resultat.ok && resultat.donnees) {
      setConfirmation(
        `Contrôle relancé le ${dateFrancaise(resultat.donnees.date)} (${resultat.donnees.id}).`,
      );
      setDateRelance('');
      setDetail({ phase: 'ferme' });
      // Liste relue tout de suite ; le calendrier suit par son propre effet.
      void charger(new AbortController().signal);
    } else {
      setErreurStatut(resultat.message ?? 'Relance refusée.');
    }
  }, [detail, dateRelance, charger]);

  const changerStatut = useCallback(async () => {
    if (detail.phase !== 'pret' || !statutChoisi) return;
    setEnregistrementStatut(true);
    setErreurStatut(undefined);

    const resultat = await modifierIntervention(
      detail.intervention.id,
      // Le motif voyage AVEC le statut : le serveur refuse l'un sans l'autre.
      statutChoisi === 'annulee'
        ? { statut: statutChoisi, motifAnnulation: motif }
        : { statut: statutChoisi },
    );
    setEnregistrementStatut(false);

    if (resultat.ok && resultat.donnees) {
      // L'état affiché est celui que le serveur renvoie, pas celui qu'on espérait.
      setDetail({ phase: 'pret', intervention: resultat.donnees });
      setStatutChoisi(undefined);
      // Libellé français, pas le code du domaine : la confirmation s'adresse à l'utilisateur.
      const libelle =
        OPTIONS_STATUT.find((option) => option.valeur === resultat.donnees?.statut)?.libelle ??
        String(resultat.donnees.statut);
      // Le serveur a pu créer le contrôle SUIVANT : on l'annonce avec SA date.
      if (resultat.donnees.prochaineOccurrence) {
        const suivante = resultat.donnees.prochaineOccurrence;
        setConfirmation(
          `Prochain contrôle planifié le ${dateFrancaise(suivante.date)}` +
            (suivante.technicien ? ` avec ${suivante.technicien}` : '') +
            '.',
        );
        setStatutChoisi(undefined);
        void charger(new AbortController().signal);
        void chargerCalendrier(moisAffiche);
        return;
      }

      setConfirmation(`Intervention ${resultat.donnees.id} : ${libelle}.`);
      void charger(new AbortController().signal);
    } else {
      setErreurStatut(resultat.message ?? 'Changement refusé.');
    }
  }, [detail, statutChoisi, motif, charger]);

  const donnees = etat.donnees;
  const filtresActifs =
    recherche !== '' || statut !== '' || retard || periode !== 'tout' || date !== '';

  // Un changement de critère invalide la sélection précédente.
  useEffect(() => {
    setSelection([]);
  }, [recherche, statut, retard, periode, date, tri, sens, page]);

  // Le calendrier est demandé au SERVEUR : les comptes par jour viennent du SQL.
  const chargerCalendrier = useCallback(async (mois: string | undefined) => {
    setCalendrier((precedent) => ({ ...precedent, chargement: true, erreur: undefined }));
    const resultat = await lireCalendrier(mois);
    if (resultat.ok && resultat.donnees) {
      setCalendrier({ donnees: resultat.donnees, chargement: false });
    } else {
      setCalendrier((precedent) => ({
        ...precedent,
        chargement: false,
        erreur: resultat.message ?? 'Erreur inattendue.',
      }));
    }
  }, []);

  useEffect(() => {
    if (vue === 'calendrier') void chargerCalendrier(moisAffiche);
  }, [vue, moisAffiche, chargerCalendrier]);

  // La charge : l'addition des minutes est faite par le serveur.
  useEffect(() => {
    if (vue !== 'charge') return;
    const controleur = new AbortController();
    setCharge((precedent) => ({ ...precedent, chargement: true, erreur: undefined }));
    void lireCharge(semaineCharge, { signal: controleur.signal })
      .then((resultat) => {
        if (resultat.ok && resultat.donnees) {
          setCharge({ donnees: resultat.donnees, chargement: false });
        } else {
          setCharge((precedent) => ({
            ...precedent,
            chargement: false,
            erreur: resultat.message ?? 'Charge indisponible.',
          }));
        }
      })
      .catch(() => {
        /* abandon : vue quittée */
      });
    return () => controleur.abort();
  }, [vue, semaineCharge]);

  // Les listes du bloc « Planning » de la fiche : chargées UNE fois, puis gardées.
  // Un formulaire d'édition ne doit pas proposer une durée que le serveur refuse.
  useEffect(() => {
    if (detail.phase !== 'pret') return;
    setPlanning(undefined);
    setErreurPlanning(undefined);
    if (refsPlanning) return;
    const controleur = new AbortController();
    void lireReferencesIntervention({ signal: controleur.signal })
      .then((resultat) => {
        if (resultat.ok && resultat.donnees) setRefsPlanning(resultat.donnees);
      })
      .catch(() => {
        /* abandon : fiche fermée */
      });
    return () => controleur.abort();
  }, [detail.phase, refsPlanning]);

  // Le serveur ramene une page hors bornes a la derniere page reelle : l'URL doit
  // dire la meme chose que l'ecran, sinon un partage donnerait un autre resultat.
  useEffect(() => {
    if (!donnees || etat.pageDemandee === undefined) return;
    if (donnees.page !== etat.pageDemandee) setPage(donnees.page);
  }, [donnees, etat.pageDemandee, setPage]);

  const effacerLesFiltres = () => {
    setSaisie('');
    toutEffacer();
  };

  const resumeAffichage = useMemo(() => {
    if (!donnees) return '';
    if (donnees.total === 0) return 'Aucune intervention';
    const debut = (donnees.page - 1) * donnees.taille + 1;
    const fin = Math.min(debut + donnees.items.length - 1, donnees.total);
    const mot = donnees.total === 1 ? 'intervention' : 'interventions';
    return `Affichage de ${debut} à ${fin} sur ${donnees.total} ${mot}`;
  }, [donnees]);

  /** En-tête triable : l'ordre est demandé au SERVEUR. */
  const enTeteTriable = (cle: TriInterventions, libelle: string) => {
    const actif = tri === cle;
    const prochainSens = actif && sens === 'desc' ? 'asc' : 'desc';
    const Icone = actif ? (sens === 'desc' ? ArrowDown : ArrowUp) : ArrowUpDown;
    return (
      <th
        scope="col"
        className="px-4 py-4 whitespace-nowrap"
        aria-sort={actif ? (sens === 'desc' ? 'descending' : 'ascending') : 'none'}
      >
        <button
          type="button"
          onClick={() => setTri(cle, prochainSens)}
          className="inline-flex items-center gap-1 hover:text-slate-800"
        >
          {libelle}
          <Icone className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
        </button>
      </th>
    );
  };

  /** Changement de statut groupé : le serveur rend le nombre RÉELLEMENT modifié. */
  const marquer = async (nouveauStatut: StatutIntervention) => {
    setErreurAction(undefined);
    const resultat = await changerStatuts(selection, nouveauStatut);
    if (resultat.ok && resultat.donnees) {
      setConfirmation(
        `${resultat.donnees.modifiees} intervention(s) passée(s) en « ${
          OPTIONS_STATUT.find((option) => option.valeur === nouveauStatut)?.libelle ?? nouveauStatut
        } ».`,
      );
      setSelection([]);
      void charger(new AbortController().signal);
    } else {
      setErreurAction(resultat.message ?? 'Changement refusé.');
    }
  };

  const toutCocher = (cocher: boolean) => {
    setSelection(cocher ? (donnees?.items ?? []).map((item) => item.id) : []);
  };

  const compteurs = donnees?.compteurs;

  return (
    <div className="space-y-6">
      {/* Indicateurs : servis par le serveur, sur TOUT le portefeuille. */}
      {compteurs && (
        <section aria-label="Indicateurs" className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            {
              libelle: 'Planifiées',
              valeur: compteurs.planifiees,
              classe: 'border-l-blue-500',
            },
            { libelle: 'En cours', valeur: compteurs.enCours, classe: 'border-l-amber-500' },
            {
              libelle: 'Terminées ce mois',
              valeur: compteurs.termineesMois,
              classe: 'border-l-emerald-500',
            },
            { libelle: 'En retard', valeur: compteurs.enRetard, classe: 'border-l-red-500' },
          ].map((indicateur) => (
            <div
              key={indicateur.libelle}
              className={`bg-white p-4 rounded-xl border border-slate-200 shadow-sm border-l-4 ${indicateur.classe}`}
            >
              <p className="text-xs font-semibold text-slate-500 uppercase">{indicateur.libelle}</p>
              <p className="text-2xl font-bold text-slate-800">{indicateur.valeur}</p>
            </div>
          ))}
        </section>
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="relative w-full md:w-80">
          <Search
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <label htmlFor="recherche-intervention" className="sr-only">
            Rechercher une intervention
          </label>
          <input
            id="recherche-intervention"
            type="search"
            value={saisie}
            onChange={(evenement) => setSaisie(evenement.target.value)}
            placeholder="Rechercher une intervention…"
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <label htmlFor="filtre-statut" className="sr-only">
            Filtrer par statut
          </label>
          <select
            id="filtre-statut"
            value={statut}
            onChange={(evenement) => setStatut(evenement.target.value as StatutIntervention | '')}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {OPTIONS_STATUT.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </select>

          <label htmlFor="filtre-periode" className="sr-only">
            Filtrer par période
          </label>
          <select
            id="filtre-periode"
            value={periode}
            onChange={(evenement) => setPeriode(evenement.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="semaine">Cette semaine</option>
            <option value="mois">Ce mois-ci</option>
            <option value="annee">Cette année</option>
            <option value="tout">Tout l’historique</option>
          </select>

          {retard && (
            <button
              type="button"
              onClick={() => setRetard(false)}
              title="Retirer le filtre « en retard »"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50"
            >
              <span aria-hidden="true">●</span>
              En retard
              <X className="w-3 h-3" aria-hidden="true" />
            </button>
          )}

          {filtresActifs && (
            <button
              type="button"
              onClick={effacerLesFiltres}
              className="px-3 py-2 border border-slate-300 text-slate-600 text-sm rounded-lg hover:bg-slate-50"
            >
              Réinitialiser
            </button>
          )}

          {/* Bascule Liste / Calendrier : deux présentations, une donnée. */}
          <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
            <button
              type="button"
              onClick={() => setVue('liste')}
              aria-pressed={vue === 'liste'}
              className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 ${
                vue === 'liste' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'
              }`}
            >
              <LayoutList className="w-3.5 h-3.5" aria-hidden="true" /> Liste
            </button>
            <button
              type="button"
              onClick={() => setVue('calendrier')}
              aria-pressed={vue === 'calendrier'}
              className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 ${
                vue === 'calendrier' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'
              }`}
            >
              <CalendarDays className="w-3.5 h-3.5" aria-hidden="true" /> Calendrier
            </button>
            <button
              type="button"
              onClick={() => setVue('charge')}
              aria-pressed={vue === 'charge'}
              className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 ${
                vue === 'charge' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'
              }`}
            >
              <Users className="w-3.5 h-3.5" aria-hidden="true" /> Charge
            </button>
          </div>

          {/* Export : le fichier est fabriqué par le SERVEUR, filtres compris. */}
          <a
            href={urlExportInterventions({ recherche, statut, retard, periode, date, tri, sens })}
            className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-2"
          >
            <Download className="w-4 h-4" aria-hidden="true" /> Exporter
          </a>

          <button
            type="button"
            onClick={() => {
              if (creation) {
                setCreation(false);
              } else {
                ouvrirCreation();
              }
            }}
            aria-expanded={creation}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
          >
            <Plus className="w-4 h-4" aria-hidden="true" />
            Nouvelle intervention
          </button>
        </div>
      </div>

      {confirmation && (
        <p role="status" className="text-sm text-green-700">
          {confirmation}
        </p>
      )}
      {erreurAction && (
        <p role="alert" className="text-sm text-red-700">
          {erreurAction}
        </p>
      )}

      {/* Jour choisi dans le calendrier : un filtre, et de quoi le retirer. */}
      {date && (
        <button
          type="button"
          onClick={() => setDate('')}
          title="Retirer le filtre de date"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50"
        >
          <span aria-hidden="true">●</span>
          Le {dateFrancaise(date)}
          <X className="w-3 h-3" aria-hidden="true" />
        </button>
      )}

      {/* Actions groupées : visibles dès qu'une ligne est cochée. */}
      {selection.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <p className="text-sm text-blue-800 font-medium">
            {selection.length} intervention{selection.length > 1 ? 's' : ''} sélectionnée
            {selection.length > 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void marquer('terminee')}
              className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-white border border-emerald-300 rounded-lg hover:bg-emerald-50 flex items-center gap-1"
            >
              <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" /> Marquer terminée
            </button>
            <button
              type="button"
              onClick={() => void marquer('annulee')}
              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 flex items-center gap-1"
            >
              <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" /> Annuler
            </button>
          </div>
        </div>
      )}

      <ModaleIntervention
        ouvert={creation}
        onFermer={() => {
          setCreation(false);
          setJourChoisi(undefined);
        }}
        onCree={(intervention) => {
          // La confirmation dit ce que le SERVEUR a retenu : son heure de fin,
          // sa durée en français, son technicien.
          const creneau =
            intervention.heureDebut && intervention.fin
              ? ` le ${dateFrancaise(intervention.date)} de ${intervention.heureDebut} à ${intervention.fin}`
              : ` le ${dateFrancaise(intervention.date)}`;
          setConfirmation(
            `Intervention ${intervention.id} planifiée pour ${intervention.client}${creneau}` +
              (intervention.technicien ? ` — ${intervention.technicien}` : '') +
              '.',
          );
          void charger(new AbortController().signal);
          void chargerCalendrier(moisAffiche);
        }}
        dateInitiale={jourChoisi}
      />

      {detail.phase !== 'ferme' && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" aria-live="polite">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              {detail.phase === 'chargement' && (
                <p className="text-sm text-slate-500">Chargement du détail…</p>
              )}

              {detail.phase === 'panne' && (
                <div role="alert">
                  <p className="font-medium text-red-700 mb-1">Détail indisponible</p>
                  <p className="text-sm text-slate-600">
                    {detail.message}{' '}
                    <button
                      type="button"
                      onClick={() => void ouvrirDetail(detail.id)}
                      className="underline text-red-700"
                    >
                      Réessayer
                    </button>
                  </p>
                </div>
              )}

              {detail.phase === 'pret' && (
                <>
                  <h2 className="text-sm font-semibold text-slate-800 mb-2">
                    {detail.intervention.id} — {detail.intervention.client}
                  </h2>
                  <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-slate-400">Date</dt>
                      <dd className="text-slate-700">{dateFrancaise(detail.intervention.date)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-slate-400">Type</dt>
                      <dd className="text-slate-700">{detail.intervention.typeIntervention}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-slate-400">Lieu</dt>
                      <dd className="text-slate-700">{detail.intervention.lieu}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-slate-400">Statut</dt>
                      <dd>
                        <BadgeStatut statut={detail.intervention.statut} />
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <label htmlFor="nouveau-statut" className="text-sm text-slate-600">
                      Changer le statut
                    </label>
                    <select
                      id="nouveau-statut"
                      value={statutChoisi ?? detail.intervention.statut}
                      onChange={(evenement) =>
                        setStatutChoisi(evenement.target.value as StatutIntervention)
                      }
                      className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      {OPTIONS_STATUT.filter((option) => option.valeur !== '').map((option) => (
                        <option key={option.valeur} value={option.valeur}>
                          {option.libelle}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => void changerStatut()}
                      disabled={enregistrementStatut || statutChoisi === undefined}
                      className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-60"
                    >
                      {enregistrementStatut ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                  </div>

                  {statutChoisi === 'annulee' && (
                    <div className="mt-2">
                      <label
                        htmlFor="motif-annulation"
                        className="block text-xs text-slate-500 mb-1"
                      >
                        Motif de l’annulation (obligatoire)
                      </label>
                      <input
                        id="motif-annulation"
                        value={motif}
                        onChange={(evenement) => setMotif(evenement.target.value)}
                        placeholder="Site fermé, report demandé par le client…"
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                  )}

                  {detail.intervention.motifAnnulation && (
                    <p className="mt-2 text-sm text-slate-600">
                      Motif d’annulation : {detail.intervention.motifAnnulation}
                    </p>
                  )}

                  {erreurStatut && (
                    <p role="alert" className="mt-2 text-sm text-red-700">
                      {erreurStatut}
                    </p>
                  )}

                  {/* Relance : un contrôle annulé se replanifie sans ressaisie. */}
                  {detail.intervention.statut === 'annulee' && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label
                            htmlFor="date-relance"
                            className="block text-xs text-slate-500 mb-1"
                          >
                            Relancer à la date du
                          </label>
                          <input
                            id="date-relance"
                            type="date"
                            value={dateRelance}
                            onChange={(evenement) => setDateRelance(evenement.target.value)}
                            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => void relancer()}
                          disabled={relance || dateRelance === ''}
                          className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 disabled:opacity-60"
                        >
                          {relance ? 'Relance…' : 'Relancer ce contrôle'}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        Le client, le type, le lieu, le créneau et le technicien sont repris.
                      </p>
                    </div>
                  )}

                  {/* ── Planning : les quatre champs du modal, corrigeables ici. */}
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-sm font-medium text-slate-700 mb-2">
                      Planning
                      {detail.intervention.fin && (
                        <span className="ml-2 text-xs font-normal text-slate-500">
                          créneau actuel : {detail.intervention.heureDebut}–
                          {detail.intervention.fin} · {detail.intervention.dureeLisible}
                        </span>
                      )}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                      <div>
                        <label
                          htmlFor="planning-heure"
                          className="block text-xs text-slate-500 mb-1"
                        >
                          Heure de début
                        </label>
                        <input
                          id="planning-heure"
                          type="time"
                          value={planning?.heure ?? detail.intervention.heureDebut ?? ''}
                          onChange={(evenement) =>
                            setPlanning({
                              heure: evenement.target.value,
                              duree: planning?.duree ?? detail.intervention.dureeMinutes ?? 60,
                              technicien:
                                planning?.technicien ?? detail.intervention.technicienId ?? '',
                              notes: planning?.notes ?? detail.intervention.notes ?? '',
                            })
                          }
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="planning-duree"
                          className="block text-xs text-slate-500 mb-1"
                        >
                          Durée
                        </label>
                        <select
                          id="planning-duree"
                          value={planning?.duree ?? detail.intervention.dureeMinutes ?? 60}
                          onChange={(evenement) =>
                            setPlanning({
                              heure: planning?.heure ?? detail.intervention.heureDebut ?? '',
                              duree: Number(evenement.target.value),
                              technicien:
                                planning?.technicien ?? detail.intervention.technicienId ?? '',
                              notes: planning?.notes ?? detail.intervention.notes ?? '',
                            })
                          }
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                        >
                          {(refsPlanning?.durees ?? []).map((duree) => (
                            <option key={duree.minutes} value={duree.minutes}>
                              {duree.libelle}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="planning-technicien"
                          className="block text-xs text-slate-500 mb-1"
                        >
                          Technicien
                        </label>
                        <select
                          id="planning-technicien"
                          value={planning?.technicien ?? detail.intervention.technicienId ?? ''}
                          onChange={(evenement) =>
                            setPlanning({
                              heure: planning?.heure ?? detail.intervention.heureDebut ?? '',
                              duree: planning?.duree ?? detail.intervention.dureeMinutes ?? 60,
                              technicien: evenement.target.value,
                              notes: planning?.notes ?? detail.intervention.notes ?? '',
                            })
                          }
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                        >
                          <option value="">À assigner plus tard</option>
                          {(refsPlanning?.techniciens ?? []).map((technicien) => (
                            <option key={technicien.id} value={technicien.id}>
                              {technicien.nom}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="planning-notes"
                          className="block text-xs text-slate-500 mb-1"
                        >
                          Consignes
                        </label>
                        <input
                          id="planning-notes"
                          value={planning?.notes ?? detail.intervention.notes ?? ''}
                          onChange={(evenement) =>
                            setPlanning({
                              heure: planning?.heure ?? detail.intervention.heureDebut ?? '',
                              duree: planning?.duree ?? detail.intervention.dureeMinutes ?? 60,
                              technicien:
                                planning?.technicien ?? detail.intervention.technicienId ?? '',
                              notes: evenement.target.value,
                            })
                          }
                          placeholder="Code portail, matériel…"
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => void enregistrerPlanning()}
                        disabled={enregistrementPlanning}
                        className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 disabled:opacity-60"
                      >
                        {enregistrementPlanning ? 'Enregistrement…' : 'Enregistrer le planning'}
                      </button>
                      <span className="text-xs text-slate-500">
                        Le conflit d’agenda est vérifié par le serveur à l’enregistrement.
                      </span>
                    </div>
                    {erreurPlanning && (
                      <p role="alert" className="mt-2 text-sm text-red-700">
                        {erreurPlanning}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => setDetail({ phase: 'ferme' })}
              aria-label="Fermer le détail"
              className="p-1 rounded hover:bg-slate-100"
            >
              <X className="w-4 h-4 text-slate-500" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Vue CALENDRIER : les comptes par jour viennent du serveur. */}
      {vue === 'calendrier' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
          {calendrier.chargement && !calendrier.donnees && (
            <p className="text-sm text-slate-500">Chargement du calendrier…</p>
          )}
          {calendrier.erreur && (
            <div role="alert" className="mb-3">
              <p className="font-medium text-red-700 mb-1">Calendrier indisponible</p>
              <p className="text-sm text-slate-600 mb-3">{calendrier.erreur}</p>
              <button
                type="button"
                onClick={() => void chargerCalendrier(undefined)}
                className="px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Réessayer
              </button>
            </div>
          )}
          {calendrier.donnees && (
            <GrilleMois
              calendrier={calendrier.donnees}
              onJour={(jour) => {
                setDate(jour);
                setVue('liste');
              }}
              // Une case VIDE ouvre le modal sur ce jour : planifier là où l'on regarde.
              onJourVide={(jour) => {
                setJourChoisi(jour);
                ouvrirCreation();
              }}
              onMois={(mois) => setMoisAffiche(mois)}
              onAujourdHui={() => setMoisAffiche(undefined)}
            />
          )}
        </div>
      )}

      {/* Vue CHARGE : chaque minute est additionnée par le serveur. */}
      {vue === 'charge' && (
        <section
          aria-label="Charge de l’équipe"
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
        >
          {charge.chargement && !charge.donnees && (
            <p className="text-sm text-slate-500">Chargement de la charge…</p>
          )}
          {charge.erreur && (
            <div role="alert">
              <p className="font-medium text-red-700 mb-1">Charge indisponible</p>
              <p className="text-sm text-slate-600 mb-3">{charge.erreur}</p>
              <button
                type="button"
                onClick={() => setSemaineCharge(semaineCharge)}
                className="px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Réessayer
              </button>
            </div>
          )}
          {charge.donnees && (
            <TableauCharge
              reponse={charge.donnees}
              onSemaine={(semaine) => setSemaineCharge(semaine)}
              onAujourdHui={() => setSemaineCharge(undefined)}
            />
          )}
        </section>
      )}

      <div
        className="bg-white rounded-lg shadow-sm border border-slate-100 overflow-hidden"
        hidden={vue !== 'liste'}
      >
        {etat.erreur && (
          <div className="p-6 border-b border-slate-100" role="alert">
            <p className="font-medium text-red-700 mb-1">Liste indisponible</p>
            <p className="text-sm text-slate-600 mb-4">{etat.erreur}</p>
            <button
              type="button"
              onClick={() => void charger(new AbortController().signal)}
              className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {!donnees && etat.chargement && !etat.erreur && (
          <p className="p-6 text-sm text-slate-500">Chargement des interventions…</p>
        )}

        {donnees && donnees.items.length === 0 && !etat.erreur && (
          <div className="p-6">
            <p className="text-sm text-slate-600 mb-3">
              {filtresActifs
                ? 'Aucune intervention ne correspond à ces critères.'
                : 'Aucune intervention enregistrée pour le moment.'}
            </p>
            {filtresActifs && (
              <button
                type="button"
                onClick={effacerLesFiltres}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
              >
                Effacer les filtres
              </button>
            )}
          </div>
        )}

        {donnees && donnees.items.length > 0 && (
          <>
            <div
              className="overflow-x-auto transition-opacity"
              aria-busy={etat.chargement}
              style={etat.chargement ? { opacity: 0.5 } : undefined}
            >
              <table className="w-full text-sm text-left">
                <caption className="sr-only">
                  Interventions du prestataire, filtrées et paginées par le serveur
                </caption>
                <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                  <tr>
                    <th scope="col" className="px-4 py-4 w-10">
                      <input
                        type="checkbox"
                        aria-label="Tout sélectionner"
                        checked={selection.length > 0 && selection.length === donnees.items.length}
                        onChange={(evenement) => toutCocher(evenement.target.checked)}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                    </th>
                    {enTeteTriable('date', 'Date')}
                    {enTeteTriable('client', 'Client')}
                    <th scope="col" className="px-4 py-4">
                      Type d’intervention
                    </th>
                    <th scope="col" className="px-4 py-4">
                      Lieu
                    </th>
                    <th scope="col" className="px-4 py-4">
                      Technicien
                    </th>
                    <th scope="col" className="px-4 py-4">
                      Statut
                    </th>
                    <th scope="col" className="px-4 py-4 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {donnees.items.map((intervention) => (
                    <tr
                      key={intervention.id}
                      className={`hover:bg-slate-50 transition-colors ${
                        selection.includes(intervention.id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          aria-label={`Sélectionner ${intervention.id}`}
                          checked={selection.includes(intervention.id)}
                          onChange={(evenement) =>
                            setSelection((precedente) =>
                              evenement.target.checked
                                ? [...precedente, intervention.id]
                                : precedente.filter((id) => id !== intervention.id),
                            )
                          }
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap font-medium text-slate-800">
                        {dateFrancaise(intervention.date)}
                        {/* Le créneau vient du serveur : heure de début + fin calculée. */}
                        {intervention.heureDebut && (
                          <span className="block text-xs font-normal text-slate-500">
                            {intervention.heureDebut}–{intervention.fin ?? '—'} ·{' '}
                            {intervention.dureeLisible}
                          </span>
                        )}
                        {(intervention.recurrenceMois ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-xs font-normal text-slate-500">
                            <Repeat className="w-3 h-3" aria-hidden="true" />
                            {intervention.recurrenceLibelle}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-slate-600">{intervention.client}</td>
                      <td className="px-4 py-4 text-slate-600">{intervention.typeIntervention}</td>
                      <td className="px-4 py-4 text-slate-600">
                        {intervention.lieu.trim() === '' ? (
                          <span className="text-slate-400">À préciser</span>
                        ) : (
                          intervention.lieu
                        )}
                      </td>
                      <td className="px-4 py-4 text-slate-600">
                        {intervention.technicien ?? (
                          <span className="text-slate-400">À assigner</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <BadgeStatut statut={intervention.statut} />
                          {/* Le retard est un FAIT daté par le serveur, pas une
                              déduction de l'horloge du navigateur. */}
                          {intervention.enRetard && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                              En retard
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right space-x-2 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => void ouvrirDetail(intervention.id)}
                          className="text-red-600 hover:text-red-800 font-medium text-xs"
                        >
                          Détails<span className="sr-only"> de {intervention.id}</span>
                        </button>
                        <button
                          type="button"
                          disabled
                          title="Le rapport d’intervention n’est pas encore livré."
                          className="text-slate-300 font-medium text-xs cursor-not-allowed"
                        >
                          Rapport
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <span className="text-xs text-slate-500">{resumeAffichage}</span>
              <Pagination page={donnees.page} pages={donnees.pages} onChange={setPage} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * La grille d'un mois, dessinée à partir des comptes du SERVEUR.
 *
 * L'écran ne compte rien : il place les jours que le serveur a rendus, et laisse
 * les autres vides. Un jour cliqué devient un filtre de la liste.
 */
function GrilleMois({
  calendrier,
  onJour,
  onJourVide,
  onMois,
  onAujourdHui,
}: {
  readonly calendrier: CalendrierMois;
  /** Un jour qui porte des interventions : on filtre la liste. */
  readonly onJour: (jour: string) => void;
  /** Un jour vide : on propose d'y planifier. */
  readonly onJourVide: (jour: string) => void;
  /** Navigation : le mois demandé est calculé par le serveur. */
  readonly onMois: (mois: string) => void;
  readonly onAujourdHui: () => void;
}) {
  const [annee = '', mois = ''] = calendrier.mois.split('-');
  const premier = new Date(`${calendrier.debut}T00:00:00Z`);
  const dernier = new Date(`${calendrier.fin}T00:00:00Z`);
  const nombreDeJours = dernier.getUTCDate();

  // La semaine commence le lundi : `getUTCDay()` rend 0 pour dimanche.
  const decalage = (premier.getUTCDay() + 6) % 7;
  const parDate = new Map(calendrier.jours.map((jour) => [jour.date, jour]));
  // Le libellé du mois VIENT DU SERVEUR (« octobre 2026 ») : deux écrans ne
  // doivent pas écrire deux fois les noms de mois.
  const libelleMois = calendrier.libelle ?? `${mois}/${annee}`;

  const cases: Array<string | null> = [
    ...Array.from({ length: decalage }, () => null),
    ...Array.from({ length: nombreDeJours }, (_, index) => {
      const jour = String(index + 1).padStart(2, '0');
      return `${annee}-${mois}-${jour}`;
    }),
  ];

  const classesStatut: Record<StatutIntervention, string> = {
    planifiee: 'bg-blue-500',
    'en-cours': 'bg-amber-500',
    terminee: 'bg-emerald-500',
    annulee: 'bg-red-500',
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => calendrier.precedent && onMois(calendrier.precedent.mois)}
            disabled={!calendrier.precedent}
            aria-label={`Mois précédent${calendrier.precedent ? ` : ${calendrier.precedent.libelle}` : ''}`}
            className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
          >
            ‹
          </button>
          <h2 className="text-base font-bold text-slate-800 min-w-40 text-center">{libelleMois}</h2>
          <button
            type="button"
            onClick={() => calendrier.suivant && onMois(calendrier.suivant.mois)}
            disabled={!calendrier.suivant}
            aria-label={`Mois suivant${calendrier.suivant ? ` : ${calendrier.suivant.libelle}` : ''}`}
            className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
          >
            ›
          </button>
          {calendrier.courant === false && (
            <button
              type="button"
              onClick={onAujourdHui}
              className="px-2.5 py-1 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50"
            >
              Aujourd’hui
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500">
          {calendrier.total === 0
            ? 'Aucune intervention ce mois-ci — cliquez un jour pour en planifier une.'
            : `${calendrier.total} intervention${calendrier.total > 1 ? 's' : ''} — cliquez un jour pour voir le détail.`}
        </p>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-slate-500 mb-1">
        {['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'].map((jour) => (
          <div key={jour} className="py-1 uppercase tracking-wide">
            {jour}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cases.map((date, index) => {
          if (!date) return <div key={`vide-${index}`} aria-hidden="true" />;
          const jour = parDate.get(date);
          const numero = Number(date.slice(8, 10));
          const aujourdHui = date === calendrier.aujourdHui;
          return (
            <button
              key={date}
              type="button"
              onClick={() => (jour ? onJour(date) : onJourVide(date))}
              title={
                jour
                  ? `${jour.nombre} intervention(s)${jour.enRetard > 0 ? `, dont ${jour.enRetard} en retard` : ''}`
                  : 'Aucune intervention — planifier ce jour'
              }
              className={`min-h-16 rounded-lg border p-1.5 text-left transition-colors ${
                jour
                  ? 'bg-slate-50 border-slate-200 hover:border-red-300 hover:bg-white'
                  : 'bg-white border-slate-100 hover:border-red-200 hover:bg-red-50/40'
              } ${aujourdHui ? 'ring-2 ring-red-400' : ''}`}
            >
              <span
                className={`block text-xs font-semibold ${aujourdHui ? 'text-red-700' : 'text-slate-600'}`}
              >
                {numero}
              </span>
              {jour && (
                <>
                  <span className="block text-sm font-bold text-slate-800">{jour.nombre}</span>
                  <span className="flex gap-0.5 mt-0.5" aria-hidden="true">
                    {jour.statuts.map((statut) => (
                      <span
                        key={statut}
                        className={`w-1.5 h-1.5 rounded-full ${classesStatut[statut]}`}
                      />
                    ))}
                    {jour.enRetard > 0 && (
                      <span className="ml-1 text-[10px] font-medium text-red-600">
                        {jour.enRetard} retard
                      </span>
                    )}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * La charge d'une semaine : une ligne par technicien, une colonne par jour.
 *
 * Aucune addition ici : chaque case affiche ce que le SERVEUR a totalisé, et la
 * ligne « non assignées » empêche un planning incomplet de se cacher.
 */
function TableauCharge({
  reponse,
  onSemaine,
  onAujourdHui,
}: {
  readonly reponse: ReponseCharge;
  readonly onSemaine: (semaine: string) => void;
  readonly onAujourdHui: () => void;
}) {
  const charge = reponse.charge;
  const jourCourt = (iso: string) => {
    const [, mois = '', jour = ''] = iso.split('-');
    return `${jour}/${mois}`;
  };
  const nomJour = (iso: string) => {
    const noms = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
    const date = new Date(`${iso}T12:00:00Z`);
    return noms[date.getUTCDay()] ?? '';
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onSemaine(reponse.precedente)}
            aria-label={`Semaine précédente : ${jourCourt(reponse.precedente)}`}
            className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
          >
            ‹
          </button>
          <h2 className="text-base font-bold text-slate-800 min-w-44 text-center">
            Semaine du {jourCourt(charge.debut)} au {jourCourt(charge.fin)}
          </h2>
          <button
            type="button"
            onClick={() => onSemaine(reponse.suivante)}
            aria-label={`Semaine suivante : ${jourCourt(reponse.suivante)}`}
            className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
          >
            ›
          </button>
          <button
            type="button"
            onClick={onAujourdHui}
            className="px-2.5 py-1 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50"
          >
            Cette semaine
          </button>
        </div>
        <p className="text-xs text-slate-500">
          {charge.totalMinutes === 0
            ? 'Aucune heure planifiée cette semaine.'
            : `${charge.totalLisible} planifiées au total.`}
        </p>
      </div>

      {charge.techniciens.length === 0 && charge.nonAssignees.interventions === 0 ? (
        <p className="text-sm text-slate-500">
          Aucune fiche technicien dans ce compte : la charge se lira dès qu’une fiche existera.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
              <tr>
                <th scope="col" className="px-3 py-3">
                  Technicien
                </th>
                {charge.techniciens[0]?.jours.map((jour) => (
                  <th key={jour.date} scope="col" className="px-3 py-3 whitespace-nowrap">
                    {nomJour(jour.date)} {jourCourt(jour.date)}
                  </th>
                ))}
                <th scope="col" className="px-3 py-3 text-right">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {charge.techniciens.map((ligne) => (
                <tr key={ligne.id ?? ligne.nom} className="hover:bg-slate-50">
                  <td className="px-3 py-3 font-medium text-slate-800">{ligne.nom}</td>
                  {ligne.jours.map((jour) => (
                    <td
                      key={jour.date}
                      className={`px-3 py-3 ${jour.minutes > 0 ? 'text-slate-700' : 'text-slate-300'}`}
                    >
                      {jour.minutes > 0 ? minutesCourt(jour.minutes) : '—'}
                      {jour.interventions > 1 && (
                        <span className="ml-1 text-xs text-slate-400">×{jour.interventions}</span>
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-3 text-right font-semibold text-slate-800">
                    {ligne.minutesLisible}
                  </td>
                </tr>
              ))}
              {charge.nonAssignees.interventions > 0 && (
                <tr className="bg-amber-50/60">
                  <td className="px-3 py-3 font-medium text-amber-900">Non assignées</td>
                  <td className="px-3 py-3 text-amber-900" colSpan={7}>
                    {charge.nonAssignees.interventions} intervention(s) —{' '}
                    {minutesCourt(charge.nonAssignees.minutes)}
                  </td>
                  <td className="px-3 py-3 text-right font-semibold text-amber-900">
                    {minutesCourt(charge.nonAssignees.minutes)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** « 150 » → « 2 h 30 » — l'affichage compact du tableau ; le total vient du serveur. */
function minutesCourt(minutes: number): string {
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  if (heures === 0) return `${reste} min`;
  if (reste === 0) return `${heures} h`;
  return `${heures} h ${String(reste).padStart(2, '0')}`;
}
